import { useMemo, useState } from 'react';
import { addWeeks, addMonths, addYears, subMonths } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle2, CreditCard, Plus, CalendarIcon, X, Trash2, List, CalendarDays, History } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { BillNegotiationReminders } from '@/components/BillNegotiationReminders';
import { BillCalendarView } from '@/components/BillCalendarView';
import { BillFrequency } from '@/lib/types';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

const PIE_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--accent))',
  'hsl(210, 70%, 55%)',
  'hsl(340, 65%, 50%)',
  'hsl(160, 60%, 45%)',
  'hsl(45, 80%, 50%)',
  'hsl(280, 55%, 55%)',
  'hsl(20, 70%, 50%)',
];

const FREQUENCIES: { value: BillFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

export default function BillsPage() {
  const { data, updateData } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [frequency, setFrequency] = useState<BillFrequency>('monthly');

  const now = new Date();
  const sorted = [...data.bills].sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  const resetForm = () => {
    setName('');
    setAmount('');
    setDueDate(undefined);
    setFrequency('monthly');
    setShowForm(false);
  };

  const handleAdd = async () => {
    if (!name.trim() || !amount || !dueDate) {
      toast.error('Please fill in all fields');
      return;
    }
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    await updateData(prev => ({
      ...prev,
      bills: [
        ...prev.bills,
        {
          id: crypto.randomUUID(),
          name: name.trim(),
          amount: parsedAmount,
          dueDate: format(dueDate, 'yyyy-MM-dd'),
          frequency,
          paid: false,
        },
      ],
    }));

    toast.success('Bill added');
    resetForm();
  };

  const markAsPaid = async (billId: string) => {
    const bill = data.bills.find(b => b.id === billId);
    if (!bill) return;
    const today = new Date().toISOString().slice(0, 10);

    // Calculate next due date based on frequency
    const currentDue = new Date(bill.dueDate);
    let nextDue: Date;
    switch (bill.frequency) {
      case 'weekly': nextDue = addWeeks(currentDue, 1); break;
      case 'fortnightly': nextDue = addWeeks(currentDue, 2); break;
      case 'monthly': nextDue = addMonths(currentDue, 1); break;
      case 'quarterly': nextDue = addMonths(currentDue, 3); break;
      case 'yearly': nextDue = addYears(currentDue, 1); break;
      default: nextDue = addMonths(currentDue, 1);
    }

    await updateData(prev => ({
      ...prev,
      bills: [
        ...prev.bills.map(b =>
          b.id === billId ? { ...b, paid: true, lastPaidDate: today } : b
        ),
        {
          id: crypto.randomUUID(),
          name: bill.name,
          amount: bill.amount,
          dueDate: format(nextDue, 'yyyy-MM-dd'),
          frequency: bill.frequency,
          paid: false,
        },
      ],
      transactions: [
        ...prev.transactions,
        {
          id: crypto.randomUUID(),
          type: 'expense' as const,
          amount: bill.amount,
          date: today,
          category: 'Bills',
          description: bill.name,
          taxDeductible: false,
          createdAt: new Date().toISOString(),
        },
      ],
    }));

    toast.success(`${bill.name} marked as paid`, {
      description: `${formatAUD(bill.amount)} logged · next due ${nextDue.toLocaleDateString('en-AU')}`,
    });
  };

  const markAsUnpaid = async (billId: string) => {
    await updateData(prev => ({
      ...prev,
      bills: prev.bills.map(b =>
        b.id === billId ? { ...b, paid: false } : b
      ),
    }));
    toast.info('Bill marked as unpaid');
  };

  const deleteBill = async (billId: string) => {
    const bill = data.bills.find(b => b.id === billId);
    await updateData(prev => ({
      ...prev,
      bills: prev.bills.filter(b => b.id !== billId),
    }));
    toast.success(`${bill?.name || 'Bill'} deleted`);
  };

  return (
    <div className="px-4 pt-6 pb-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Bills</h1>
        <Button size="sm" className="gap-1.5" onClick={() => setShowForm(!showForm)}>
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? 'Cancel' : 'Add Bill'}
        </Button>
      </div>

      {showForm && (
        <Card className="mb-4">
          <CardContent className="grid gap-3 p-4">
            <div>
              <Label className="text-xs">Name</Label>
              <Input placeholder="e.g. Electricity" value={name} onChange={e => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Amount (AUD)</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('mt-1 w-full justify-start text-left font-normal', !dueDate && 'text-muted-foreground')}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, 'dd/MM/yyyy') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs">Frequency</Label>
              <Select value={frequency} onValueChange={v => setFrequency(v as BillFrequency)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAdd} className="mt-1">Add Bill</Button>
          </CardContent>
        </Card>
      )}

      <BillNegotiationReminders bills={data.bills} />

      <Tabs defaultValue="list" className="w-full">
        <TabsList className="mb-3 w-full">
          <TabsTrigger value="list" className="flex-1 gap-1.5">
            <List className="h-3.5 w-3.5" />
            List
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex-1 gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="history" className="flex-1 gap-1.5">
            <History className="h-3.5 w-3.5" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {sorted.length === 0 && !showForm ? (
            <div className="mt-12 text-center">
              <p className="text-muted-foreground">No bills yet</p>
              <p className="text-sm text-muted-foreground">Tap "Add Bill" to start tracking</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sorted.map(bill => {
                const overdue = !bill.paid && new Date(bill.dueDate) < now;
                return (
                  <Card key={bill.id} className={cn(overdue && 'border-destructive/50')}>
                    <CardContent className="flex items-center gap-3 p-3">
                      {overdue ? (
                        <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
                      ) : bill.paid ? (
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
                      ) : (
                        <div className="h-5 w-5 shrink-0 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{bill.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Due {new Date(bill.dueDate).toLocaleDateString('en-AU')} · {bill.frequency}
                          {bill.paid && bill.lastPaidDate && (
                            <span className="text-success"> · Paid {new Date(bill.lastPaidDate).toLocaleDateString('en-AU')}</span>
                          )}
                        </p>
                      </div>
                      <p className="font-display font-semibold shrink-0">{formatAUD(bill.amount)}</p>
                      <div className="flex gap-1 shrink-0">
                        {bill.paid ? (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => markAsUnpaid(bill.id)} title="Mark as unpaid">
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => markAsPaid(bill.id)} title="Mark as paid">
                            <CreditCard className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => deleteBill(bill.id)} title="Delete bill">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <BillCalendarView
            bills={data.bills}
            onMarkPaid={markAsPaid}
            onMarkUnpaid={markAsUnpaid}
            onDelete={deleteBill}
          />
        </TabsContent>

        <TabsContent value="history">
          {(() => {
            const paidBillTxns = data.transactions
              .filter(t => t.type === 'expense' && t.category === 'Bills')
              .sort((a, b) => b.date.localeCompare(a.date));

            const totalPaid = paidBillTxns.reduce((s, t) => s + t.amount, 0);

            // Build monthly chart data (last 6 months)
            const monthlyData: { month: string; total: number }[] = [];
            for (let i = 5; i >= 0; i--) {
              const m = subMonths(new Date(), i);
              const key = format(m, 'yyyy-MM');
              const label = format(m, 'MMM');
              const total = paidBillTxns
                .filter(t => t.date.startsWith(key))
                .reduce((s, t) => s + t.amount, 0);
              monthlyData.push({ month: label, total: Math.round(total * 100) / 100 });
            }

            if (paidBillTxns.length === 0) {
              return (
                <div className="mt-12 text-center">
                  <p className="text-muted-foreground">No bill payments yet</p>
                  <p className="text-sm text-muted-foreground">Paid bills will appear here</p>
                </div>
              );
            }

            return (
              <div className="space-y-3">
                <Card>
                  <CardContent className="flex items-center justify-between p-3">
                    <p className="text-sm text-muted-foreground">Total paid</p>
                    <p className="font-display text-lg font-bold">{formatAUD(totalPaid)}</p>
                  </CardContent>
                </Card>

                {/* Monthly spending chart */}
                <Card>
                  <CardContent className="p-3">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">Monthly Bill Spending (6 months)</p>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={monthlyData} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                        <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" tickFormatter={v => `$${v}`} />
                        <Tooltip
                          formatter={(value: number) => [formatAUD(value), 'Bills']}
                          contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        />
                        <Bar dataKey="total" className="fill-primary" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Bill breakdown pie chart */}
                {(() => {
                  const byName = new Map<string, number>();
                  paidBillTxns.forEach(t => {
                    byName.set(t.description.replace(/^\[basiq:[^\]]+\]\s*/, ''), (byName.get(t.description.replace(/^\[basiq:[^\]]+\]\s*/, '')) || 0) + t.amount);
                  });
                  const pieData = Array.from(byName.entries())
                    .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
                    .sort((a, b) => b.value - a.value);

                  return (
                    <Card>
                      <CardContent className="p-3">
                        <p className="mb-2 text-xs font-medium text-muted-foreground">Spending by Bill</p>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              innerRadius={40}
                              paddingAngle={2}
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              style={{ fontSize: 10 }}
                            >
                              {pieData.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => formatAUD(value)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  );
                })()}

                <div className="space-y-2">
                  {paidBillTxns.map(txn => (
                    <Card key={txn.id}>
                      <CardContent className="flex items-center gap-3 p-3">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{txn.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(txn.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <p className="font-display text-sm font-semibold shrink-0">{formatAUD(txn.amount)}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
